import { checkReportRange, kysDocumentNo, kysFormLabel, kysFr12Lines, type KysFr12Form, type ReportResult } from "@fts/core";
import { useState } from "react";
import { ConfirmDialog } from "../../components/Dialog";
import { Icon } from "../../components/Icon";
import { EmptyState, Note, PageHeader, Panel } from "../../components/ui";
import { useI18n } from "../../i18n";
import { formatDateTimeIn, formatDay } from "../../lib/export/sheet";
import { fr12FormExport, fr12Sheet, fr12Signatures } from "../../lib/kys-forms";
import { canManageFleet } from "../../lib/permissions";
import { navigate, type Route } from "../../router/hash";
import { useSession, type TenantData } from "../../state/session";
import "../../styles/kys.css";
import { ResultBadge } from "../ChecklistParts";
import { FormFooterButton, useFormFooter } from "./KysParts";
import { PrintBrand, ReportContext, ReportEmpty } from "./ReportParts";
import { ExportBar, FilterBar, Loading, contextChips, filterSummary, filtersQuery, parseFilters, rangeKey, siteLabel, useLoad, type ExportSpec } from "./shared";

/**
 * KYS FR-12 "Günlük Forklift Kontrol" (ADR 0033): the filled-in FR-12 forms of a period, each one viewable
 * in the paper form's layout, approvable by fleet managers+ ("Onaylayan" = role + time) and exportable as
 * the form-shaped PDF (one form or the whole period) or XLSX. No operator names (KVKK). Never locks a vehicle.
 */
export function KysFr12Report({ data, route }: { data: TenantData; route: Route }) {
  const { t, lang } = useI18n();
  const { provider, tenant, role } = useSession();
  const f = parseFilters(route, data);
  const doc = route.query.get("doc");
  const issue = checkReportRange(f.from, f.to);
  const tenantId = tenant?.id;
  const [tick, setTick] = useState(0);
  const query = (id: string) => ({ tenantId: id, siteId: f.siteId, vehicleIds: f.vehicleIds, from: f.from, to: f.to });
  const state = useLoad<ReportResult<KysFr12Form>>(tenantId && !issue ? () => provider.reportKysFr12(query(tenantId)) : null, [provider, tenantId, f.siteId, f.vehicleIds.join(","), f.from, f.to, tick]);
  // A single form opened from the vehicle page (?doc=…): loaded by id, whatever the period.
  const single = useLoad<ReportResult<KysFr12Form>>(tenantId && doc ? () => provider.reportKysFr12({ ...query(tenantId), submissionId: doc }) : null, [provider, tenantId, doc, tick]);
  const rows = state.data?.rows ?? [];
  const tz = state.data?.tz ?? "Europe/Istanbul";
  const selected = single.data?.rows[0] ?? null;
  const filters = filterSummary(t, data, f, { operator: false });
  const { footer, reload: reloadFooter } = useFormFooter();
  const keep = (extra: Record<string, string | undefined>) => ({ ...filtersQuery(f), ...extra });

  const spec: ExportSpec | null = state.status === "ready" && tenantId
    ? {
        report: "kys_fr12",
        reportName: t("kys.fr12.title"),
        slug: "kys-fr12",
        part: `${f.from}_${f.to}`,
        tz,
        filters,
        rows: rows.length,
        load: async (format) => {
          const r = await provider.exportKysFr12(query(tenantId), format);
          return { sheets: [fr12Sheet(t, lang, r.rows)], tz: r.tz, form: fr12FormExport(t, lang, r.rows, r.tz, footer) };
        },
      }
    : null;
  const singleSpec: ExportSpec | null = selected && tenantId
    ? {
        report: "kys_fr12",
        reportName: t("kys.fr12.title"),
        slug: "kys-fr12",
        part: kysDocumentNo(selected.form.code, selected.documentNo) ?? selected.submissionId,
        tz,
        filters: [[t("kys.fr12.field.documentNo"), kysDocumentNo(selected.form.code, selected.documentNo) ?? "—"]],
        rows: 1,
        load: async (format) => {
          const r = await provider.exportKysFr12({ ...query(tenantId), submissionId: selected.submissionId }, format);
          return { sheets: [fr12Sheet(t, lang, r.rows)], tz: r.tz, form: fr12FormExport(t, lang, r.rows, r.tz, footer) };
        },
      }
    : null;
  const rk = rangeKey(issue);

  return (
    <>
      <PrintBrand title={t("kys.fr12.title")} tz={state.data?.tz ?? null} />
      <PageHeader title={t("kys.fr12.title")} subtitle={t("kys.fr12.subtitle")} eyebrow={t("nav.group.kys")} />
      <FilterBar data={data} path="/reports/kys-fr12" filters={f} operator={false} />
      {rk && (
        <p className="form-error" role="alert">
          {t(rk)}
        </p>
      )}
      <ReportContext chips={contextChips(t, filters)} from={f.from} to={f.to} tz={state.data?.tz ?? null} sample={provider.mode === "demo"} />
      <div className="kys-actions">
        <ExportBar spec={spec} disabled={rows.length === 0} buttons={["xlsx", "pdf"]} pdfLabel={t("kys.export.pdfAll")} />
        <FormFooterButton footer={footer} onSaved={reloadFooter} />
      </div>
      {state.status === "error" && (
        <p className="form-error" role="alert">
          {state.error}
        </p>
      )}
      {doc && (
        <Panel
          title={selected ? `${kysDocumentNo(selected.form.code, selected.documentNo) ?? ""} · ${selected.fleetNo}` : t("kys.fr12.formTitle")}
          meta={
            <button type="button" className="link-button" onClick={() => navigate("/reports/kys-fr12", keep({ doc: undefined }))}>
              {t("kys.fr12.closeForm")}
            </button>
          }
        >
          {single.status === "error" ? (
            <p className="form-error">{single.error}</p>
          ) : !selected ? (
            single.status === "ready" ? <EmptyState compact icon="info" title={t("kys.fr12.notFound")} /> : <Loading />
          ) : (
            <>
              <Fr12Paper form={selected} tz={tz} footer={footer} />
              <div className="kys-actions">
                {canManageFleet(role) && !selected.approvedAt && <ApproveButton submissionId={selected.submissionId} onDone={() => setTick((x) => x + 1)} />}
                <ExportBar spec={singleSpec} buttons={["pdf"]} pdfLabel={t("kys.export.pdfOne")} />
              </div>
            </>
          )}
        </Panel>
      )}
      <Panel flush meta={state.status === "ready" ? <span>{t("kys.fr12.count", { count: rows.length })}</span> : undefined}>
        {state.status !== "ready" && state.status !== "error" ? (
          rk ? <EmptyState compact icon="info" title={t(rk)} /> : <Loading />
        ) : rows.length === 0 ? (
          state.status === "error" ? null : <ReportEmpty path="/reports/kys-fr12" filters={f} siteName={siteLabel(t, data, f.siteId)} />
        ) : (
          <div className="table-wrap table-scroll">
            <table className="table report-table table-cards">
              <thead>
                <tr>
                  <th scope="col">{t("kys.fr12.field.documentNo")}</th>
                  <th scope="col">{t("kys.fr12.field.date")}</th>
                  <th scope="col">{t("reports.col.vehicle")}</th>
                  <th scope="col">{t("kys.fr12.field.result")}</th>
                  <th scope="col">{t("kys.fr12.sign.approver")}</th>
                  <th scope="col">
                    <span className="sr-only">{t("kys.fr12.open")}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.submissionId} className={r.submissionId === doc ? "is-selected" : undefined}>
                    <td className="mono cell-title" data-label={t("kys.fr12.field.documentNo")}>{kysDocumentNo(r.form.code, r.documentNo) ?? "—"}</td>
                    <td className="nowrap" data-label={t("kys.fr12.field.date")}>{formatDateTimeIn(r.submittedAt, tz, lang)}</td>
                    <td className="mono" data-label={t("reports.col.vehicle")}>{r.fleetNo}</td>
                    <td data-label={t("kys.fr12.field.result")}>
                      <ResultBadge result={r.result} />
                    </td>
                    <td data-label={t("kys.fr12.sign.approver")}>{fr12Signatures(t, r, tz, lang)[1]![1]}</td>
                    <td className="col-actions">
                      <button type="button" className="link-button" onClick={() => navigate("/reports/kys-fr12", keep({ doc: r.submissionId }))}>
                        {t("kys.fr12.open")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      <p className="panel-footnote">{t("kys.fr12.footnote")}</p>
    </>
  );
}

/** The filled-in FR-12 in the paper form's layout (screen and print). */
export function Fr12Paper({ form, tz, footer }: { form: KysFr12Form; tz: string; footer: string | null }) {
  const { t, lang } = useI18n();
  const doc = fr12FormExport(t, lang, [form], tz, footer);
  const d = doc.documents[0]!;
  const lines = kysFr12Lines(form, lang, t("kys.fr12.na"));
  return (
    <article className="kys-paper" aria-label={`${kysFormLabel(form.form)} ${kysDocumentNo(form.form.code, form.documentNo) ?? ""}`}>
      <header className="kys-paper-head">
        <img src={`${import.meta.env.BASE_URL}brand/expartise-logo.png`} alt={t("app.logoAlt")} width={110} height={35} />
        <dl>
          <div><dt>{doc.labels.code}</dt><dd>{d.ref.code}</dd></div>
          <div><dt>{doc.labels.revision}</dt><dd>{d.ref.revision}</dd></div>
          <div><dt>{doc.labels.revisionDate}</dt><dd>{d.ref.revisionDate ? formatDay(d.ref.revisionDate, lang) : "—"}</dd></div>
        </dl>
      </header>
      <h2 className="kys-paper-title">{d.ref.title}</h2>
      <dl className="kys-paper-fields">
        {d.fields.map(([k, v]) => (
          <div key={k}><dt>{k}</dt><dd>{v}</dd></div>
        ))}
      </dl>
      <table className="kys-paper-table">
        <caption className="sr-only">{d.table.heading}</caption>
        <thead>
          <tr>
            <th scope="col">{t("kys.fr12.col.item")}</th>
            <th scope="col" className="kys-mark">{t("kys.fr12.col.ok")}</th>
            <th scope="col" className="kys-mark">{t("kys.fr12.col.notOk")}</th>
            <th scope="col">{t("kys.fr12.col.note")}</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.code} className={l.notOk ? "is-failed" : l.na ? "is-na" : undefined}>
              <td>{l.text}</td>
              <td className="kys-mark">{l.ok ? <Icon name="check" size={16} /> : null}<span className="sr-only">{l.ok ? t("checklist.answer.ok") : ""}</span></td>
              <td className="kys-mark">{l.notOk ? <Icon name="close" size={16} /> : null}<span className="sr-only">{l.notOk ? t("checklist.answer.not_ok") : ""}</span></td>
              <td>{l.note}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="kys-paper-signs">
        {d.signatures.map(([k, v]) => (
          <div key={k}>
            <div className="kys-sign-label">{k}</div>
            <div className="kys-sign-value">{v}</div>
          </div>
        ))}
      </div>
      <p className="kys-paper-foot">{doc.labels.controlled}</p>
      <Note>{t("kys.fr12.noLock")}</Note>
    </article>
  );
}

/** "Onayla": fleet managers+ of the site (the server checks the scope); once. */
export function ApproveButton({ submissionId, onDone }: { submissionId: string; onDone: () => void }) {
  const { t } = useI18n();
  const { provider } = useSession();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="button button-primary" onClick={() => setOpen(true)}>
        <Icon name="check" size={16} /> {t("kys.fr12.approve")}
      </button>
      {open && (
        <ConfirmDialog
          title={t("kys.fr12.approveTitle")}
          confirmLabel={t("kys.fr12.approve")}
          successMessage={t("kys.fr12.approved")}
          onClose={() => setOpen(false)}
          onConfirm={async () => {
            await provider.approveChecklistSubmission(submissionId);
            setOpen(false);
            onDone();
          }}
        >
          {t("kys.fr12.approveText")}
        </ConfirmDialog>
      )}
    </>
  );
}
